import { Component, ElementRef, ViewChild,OnInit,OnDestroy, NgZone, ChangeDetectionStrategy   } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RbacService, ServerlessOAuthConfig, ServerlessOauthService } from '@stg-js-ngx-core/oauth';
import { GoogleAnalyticsService } from './common/services/google-analytics.service';
import { COMBINED_MENU, HOSPITAL_SELECTOR_DISABLED_PAGES, HOSPITAL_SELECTOR_PAGES } from './common/mmt-constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalRecommendationComponent } from './common/components/modal-recommendation/modal-recommendation.component';
import { ToastrService } from 'ngx-toastr';
import { CommonModalComponent } from './common/components/common-modal/common-modal.component';
import { BlockUI, NgBlockUI } from './common/block-ui';
import { UserAdministrationService } from './common/services/user-administration.service';
import { MmtCommonService } from './common/services/mmt-common.service';
import { AppConfigService } from './appServiceConfig';
import { CookieService } from './common/services/cookie.service';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy  {
  public showPopup: boolean = false;
  private timerLeft: number = 5;
  private autoSaveInterval: any;
  title: string = 'my-malnutrition-tools-ui-ngx';
  appLoad: boolean = false;
  showRouter: boolean = false;
  showHospitalSelector: boolean = false;
  hospitalSelectorDisabled: boolean = false;
  showHierarchySelector: boolean = false;
  hierarchySelectorDisabled: boolean = false;
  isNotUnauthorized: boolean = true;
  logo: string = '/assets/images/MyMalnutritionToolsLogoC.png';
  profileImageUrl: string = '/assets/images/genericAvatar.png';
  menuItems: any = [];
  profileItems: any = [];
  private roleChangeSub!: Subscription;

  @BlockUI('dashboard-page') blockUI: NgBlockUI;
  @ViewChild('pageHeader') pageHeader: ElementRef;

  oauthConfig: ServerlessOAuthConfig = {
    serverless: true,
    clientId: this.appConfigService.getConfig().clientId,
    rbacEnabled: true,
    rbacAppName: this.appConfigService.getConfig().rbacAppName,
    revalidateAfterMin: 30,
    sessionExpirationAlertInMin: 3,
    longestTimeToLiveHours: 8,
    autoLogoutOnSessionExpired: true,
    logoutUrl: this.appConfigService.getConfig().baseUrls.logout,
    environmentConfig: this.appConfigService.getConfig()
  };

  constructor(
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private serverlessOauthService: ServerlessOauthService,
    private rbacService: RbacService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private modalService: NgbModal,
    private userAdminservice: UserAdministrationService,
    private toastrService: ToastrService,
    private commonService: MmtCommonService,
    private appConfigService: AppConfigService,
    private cookieService: CookieService,
    private ngZone: NgZone
  ) {
    const existingToken = this.cookieService.get('host-token');

    if (existingToken) {
      this.commonService.oAuthToken = existingToken;
    }
  }

  ngOnInit(): void {
    this.menuItems = COMBINED_MENU;

    // Listen for role changes to update menu
    // The oauth library settles this promise outside the Angular zone; without ngZone.run the
    // whole chain below (and its HttpClient callbacks) never triggers change detection.
    this.serverlessOauthService.beginServerlessOauthHandling(this.oauthConfig)
      .then(() => this.ngZone.run(() => this.loadApp()))
      .catch(() => this.ngZone.run(() => { this.appLoad = false; }));

    this.serverlessOauthService.getOauthProfile();

    // Re-fetch hierarchical teams when role changes (e.g. role dropdown switch)
    this.roleChangeSub = this.rbacService.roleChangeObservable.subscribe((profile: any) => {
      if (profile && profile.current_role) {
        this.onRoleChanged(profile);
      }
    });
  }

  loadApp() {
    const oauthProfile: any = this.serverlessOauthService.getOauthProfile();
    const existingToken = this.cookieService.get('host-token');

    if (!existingToken && !(oauthProfile && oauthProfile.token) && window.location.href.indexOf('token') === -1) {
      // No auth — should not happen if OAuth component is working
      this.appLoad = false;
      return;
    }

    // Store token in cookie for session persistence
    if (!existingToken && oauthProfile && oauthProfile.token) {
      this.cookieService.set('host-token', oauthProfile.token, {
        expires: new Date(new Date().getTime() + 1000 * 60 * 30),
        path: '/',
        secure: true,
        sameSite: 'Strict'
      });
      this.commonService.oAuthToken = oauthProfile.token;
    } else if (existingToken) {
      this.commonService.oAuthToken = existingToken;
    }

    this._cleanUrl();
    this._restoreDeepLink();

    // Fetch full RBAC profile from .NET API (server-side ADAMS call)
    // Must complete before rendering header so roles dropdown is populated
    this.commonService.getRbacProfileCustom().subscribe({
      next: (res: any) => {
        if (res && res.data) {
          const rbacProfile = res.data;

          // hierarchy-tree lib does JSON.parse(JSON.stringify(...)) over rbacService.teams and
          // will throw "SyntaxError: undefined is not valid JSON" if teams is null/undefined.
          // Normalize to [] here; the real teams are set below once getHierarchicalTeams returns.
          if (!rbacProfile.teams) {
            rbacProfile.teams = [];
          }

          // Set profile on the library's RbacService so HeaderComponent can read it
          this.rbacService.currentProfile = rbacProfile;

          // Render the app now that RBAC profile (with all_roles) is available
          this.appLoad = true;
          this.showRouter = true;
          this.blockUI.start();

          this.googleAnalyticsService.initializeGA(
            this.appConfigService.getConfig().ga.id,
            rbacProfile.user_name?.toUpperCase() || ''
          );
          this.trackPageNavigation();

          if (rbacProfile.all_roles && rbacProfile.all_roles.length > 0) {
            // Fetch hierarchical teams and their children in parallel
            const roleName = rbacProfile.current_role?.role_name || rbacProfile.all_roles[0]?.role_name;
            const userName = rbacProfile.user_name;
            const appName = this.appConfigService.getConfig().rbacAppName;

            this.commonService.getHierarchicalTeams(appName, roleName, userName).subscribe({
              next: (teamsRes: any) => {
                const teams = teamsRes?.data || teamsRes;
                if (teams && Array.isArray(teams) && teams.length > 0) {
                  this.initializeHierarchy(rbacProfile, teams);
                } else {
                  this.blockUI.stop();
                }
              },
              error: (err) => {
                console.error('Error fetching hierarchical teams:', err);
                this.blockUI.stop();
              }
            });

            // Check if user can see profile
            if (
              rbacProfile.user_name &&
              rbacProfile.email &&
              rbacProfile.user_name.toLowerCase() === rbacProfile.email.toLowerCase()
            ) {
              this.profileItems.push({ iconClass: 'fas fa-user-alt', page: '/profile', title: 'My Profile' });
            }
          } else {
            this.appLoad = true;
            this.showRouter = true;
            this.userAdminservice.getOMSAccess()
              .subscribe(omsRes => {
                this.router.navigate(['/unauthorized']);
                this.popupMaxInterval(omsRes);
                this.blockUI.stop();
              }, error => {
                console.error(error);
                this.blockUI.stop();
                this.toastrService.error('Could not redirect to compass manager.');
              });
          }
        } else {
          this.appLoad = true;
          this.showRouter = true;
          this.blockUI.stop();
          this.userAdminservice.getOMSAccess()
            .subscribe(omsRes => {
              this.router.navigate(['/unauthorized']);
              this.popupMaxInterval(omsRes);
            }, error => {
              console.error(error);
              this.toastrService.error('Could not redirect to compass manager.');
            });
        }
      },
      error: (error: any) => {
        console.error('Error fetching RBAC profile:', error);
        this.appLoad = true;
        this.showRouter = true;
        this.blockUI.stop();
        this.toastrService.error('Error occurred with ADAMS. Please contact Administrator.');
      }
    });
  }

  openRecommendation() {
    this.modalService.open(ModalRecommendationComponent, {
      size: 'lg', backdrop: 'static',
      keyboard: false
    });
  }

  private onRoleChanged(profile: any): void {
    const roleName = profile.current_role?.role_name;
    const userName = profile.user_name;
    const appName = this.appConfigService.getConfig().rbacAppName;

    this.blockUI.start();
    this.commonService.getHierarchicalTeams(appName, roleName, userName).subscribe({
      next: (teamsRes: any) => {
        const teams = teamsRes?.data || teamsRes;
        if (teams && Array.isArray(teams) && teams.length > 0) {
          this.initializeHierarchy(profile, teams);
        } else {
          // No teams for this role — update profile and menu without hierarchy
          profile.teams = [];
          this.rbacService.currentProfile = profile;
          this.rbacService.teams = [];
          this.showHierarchySelector = false;
          this.commonService.getDefaultMenu([])
            .subscribe(out => {
              this.menuItems = out;
              this.blockUI.stop();
            }, () => this.blockUI.stop());
        }
      },
      error: (err) => {
        console.error('Error fetching teams after role change:', err);
        this.blockUI.stop();
      }
    });
  }

  private initializeHierarchy(rbacProfile: any, teams: any[]) {
    // Update the profile with teams so library's selectedTeam getter works
    rbacProfile.teams = teams;
    this.rbacService.currentProfile = rbacProfile;
    this.rbacService.teams = teams;
    // Re-evaluate against current URL so hierarchy only shows on allowed pages
    this.updateHierarchySelector(this.router.url);
    this.updateHospitalSelector(this.router.url);

    // Auto-select default team and notify dashboard
    const defaultTeam = this.commonService.getDefaultHierarchy();
    if (defaultTeam) {
      this.commonService.hierarchy = defaultTeam.team_name;
      this.commonService.selectedTeamDefaultValue = defaultTeam.team_name;
      this.commonService.hierarchyChange.next([{ team_name: defaultTeam.team_name, team_description: defaultTeam.team_description }]);
    }

    // Load menu based on fetched teams
    this.commonService.getDefaultMenu(teams)
      .subscribe(out => {
        this.menuItems = out;
        this.blockUI.stop();
      },
      (err) => {
        this.blockUI.stop();
        console.error('Error while loading menu - ' + err);
      });
  }

  private trackPageNavigation() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateHospitalSelector(event.url);
        this.updateHierarchySelector(event.url);
        this.updateUnauthorizedState(event.url);
        this.updateHeaderForPreview(event.url);
        this.updatePageTitle();
        this.googleAnalyticsService.setAnalyticsForRoutes(event.urlAfterRedirects);
      }
    });
    // Apply title for the route that was already active when this app bootstrapped
    // (the page-header library misses the initial NavigationEnd because it is
    // created after appLoad becomes true).
    setTimeout(() => this.updatePageTitle(), 0);
  }

  private updatePageTitle(): void {
    if (!this.pageHeader) {
      return;
    }
    const route = this.getDeepestChildRoute(this.activatedRoute);
    const { title, backPage } = this.extractTitleAndBackPage(route);
    const header: any = this.pageHeader;
    header.title = title || '';
    header.backPageUrl = backPage;
  }

  private getDeepestChildRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private extractTitleAndBackPage(route: ActivatedRoute): { title: string | null, backPage: string | null } {
    let title: string | null = null;
    let backPage: string | null = null;
    let r: ActivatedRoute | null = route;
    while (r) {
      const data: any = r.snapshot && r.snapshot.data;
      if (data) {
        if (title === null && data.pageTitle) {
          title = data.pageTitle;
        }
        if (backPage === null && data.backPage) {
          backPage = data.backPage;
        }
      }
      if (title !== null) { break; }
      r = r.parent;
    }
    return { title, backPage };
  }

  private updateHospitalSelector(url: string): void {
    // Hierarchy and facility selectors are mutually exclusive
    if (this.isHierarchyUrl(url)) {
      this.showHospitalSelector = false;
      this.hospitalSelectorDisabled = false;
      return;
    }
    if ((url.indexOf('/patientdata/inpatient/details') > -1) || (url.indexOf('/patientdata/afterdischarge/details') > -1) || (url.indexOf('/patientdata/denialdata/details') > -1)
      || (url.indexOf('/patientdata/residentdata/details') > -1) || (url.indexOf('/patientdata/nutritionchangedata/details') > -1) || (url.indexOf('/patientdata/medicarepaymentdata/details') > -1)
      || (url.indexOf('/patientdata/rehabilitation/details') > -1)
      || (url.indexOf('/patientdata/ltachinpatient/details') > -1) || (url.indexOf('/patientdata/ltachafterdischarge/details') > -1)
      || (url.indexOf('/patientdata/pediatricinpatient/details') > -1) || (url.indexOf('/patientdata/pediatricafterdischarge/details') > -1)
      || (url.indexOf('/patientdata/pediatricdenialdata/details') > -1)) {
      this.showHospitalSelector = true;
      this.hospitalSelectorDisabled = true;
    } else {
      this.showHospitalSelector = HOSPITAL_SELECTOR_PAGES.indexOf(url.toString()) > -1;
      this.hospitalSelectorDisabled = HOSPITAL_SELECTOR_DISABLED_PAGES.indexOf(url.toString()) > -1;
    }
  }

  private updateHierarchySelector(url: string): void {
    this.hierarchySelectorDisabled = false;
    if (this.isHierarchyUrl(url)) {
      this.showHierarchySelector = !!(this.rbacService.teams && (this.rbacService.teams as any[]).length > 0);
    } else {
      this.showHierarchySelector = false;
    }
  }

  private isHierarchyUrl(url: string): boolean {
    if (!url) { return false; }
    const path = url.split('?')[0];
    return path === '/' || path === '/home' || path.startsWith('/home/');
  }

  private updateUnauthorizedState(url: string): void {
    if (url.indexOf('/unauthorized') > -1) {
      this.isNotUnauthorized = false;
      this.showHospitalSelector = false;
      this.showHierarchySelector = false;
    } else {
      this.isNotUnauthorized = true;
    }
  }

  private updateHeaderForPreview(url: string): void {
    if (url && url.indexOf('manual/acute') > -1) {
      setTimeout(() => this.changeHeaderForPreview('Acute Care Manual'), 0);
    }
    if (url && url.indexOf('manual/ltc') > -1) {
      setTimeout(() => this.changeHeaderForPreview('Long Term Care Manual'), 0);
    }
  }

  private changeHeaderForPreview(title: string) {
    if (this.pageHeader) {
      let pageHeading: any = this.pageHeader;
      pageHeading.title = title;
    }
  }

  loadUnauthorizationPopup(data: any, showPopup: boolean) {
    if (showPopup) {
      let modal = this.modalService.open(CommonModalComponent, {
        keyboard: false,
        backdrop: 'static'
      });

      modal.componentInstance.title = 'Unauthorized';
      modal.componentInstance.bodyText = 'You are not authorized in (MMT)-MyMalnutritionTools.';
      modal.componentInstance.okText = 'OK';
      modal.componentInstance.showCancel = false;

      return modal.result.then(() => {
        window.location.href = data;
        return true;
      }).catch(() => { window.location.href = data; return false; });
    } else {
      window.location.href = data;
      return false;
    }
  }

  popupMaxInterval(data: any) {
    clearInterval(this.autoSaveInterval);
    this.timerLeft = 5;
    this.showPopup = true;
    this.loadUnauthorizationPopup(data, this.showPopup);
    this.autoSaveInterval = setInterval(() => { this.hidePopup(data); }, 1000);
  }

  hidePopup(data: any) {
    if (this.timerLeft > 0) {
      this.timerLeft--;
    } else {
      this.timerLeft = 5;
      clearInterval(this.autoSaveInterval);
      this.showPopup = false;
      window.location.href = data;
    }
  }

  ngOnDestroy(): void {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
    if (this.roleChangeSub) {
      this.roleChangeSub.unsubscribe();
    }
  }

  private _cleanUrl(): void {
    const href = location.origin + location.pathname
      + location.search.replace(/&?token=[^&$]*/, '')
        .replace(/&$/, '')
        .replace(/^\?$/, '')
        .replace(/&+/g, '&')
        .replace(/\?&/, '?')
        .replace(/\?$/, '')
      + location.hash;
    history.replaceState(null, window.name, href);
  }

  // Navigate to the originally requested URL captured pre-bootstrap (main.ts).
  // The OAuth library navigates to '/' after auth, which we override here so
  // deep links like /displaypdf/83 reach their intended route.
  private _restoreDeepLink(): void {
    let target: string | null = null;
    try {
      target = sessionStorage.getItem('mmt.deepLink');
      if (target) {
        sessionStorage.removeItem('mmt.deepLink');
      }
    } catch {
      return;
    }
    if (!target) {
      return;
    }
    // setTimeout(0) defers our navigate until after any pending router event
    // (the library schedules router.navigate([]) right before emitting onOauthLoad).
    setTimeout(() => {
      const current = location.pathname + location.search;
      if (current !== target) {
        this.router.navigateByUrl(target);
      }
    }, 0);
  }
}
